'use client'

import { Loader2 } from 'lucide-react'
import * as React from 'react'
import { UseFormReturn } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp'

import type { PasswordResetFormData } from '../hooks/usePasswordResetFlow'

interface CodeStepProps {
  form: UseFormReturn<PasswordResetFormData>
  onSubmit: (data: PasswordResetFormData) => Promise<void> | void
  onResend: () => Promise<void> | void
  isLoading: boolean
  email: string
}

export function CodeStep({ form, onSubmit, onResend, isLoading, email }: CodeStepProps) {
  const codeValue = form.watch('code')
  const isSubmitDisabled = isLoading || !codeValue || codeValue.length !== 6

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2 text-center">
          <p className="text-sm text-gray-600">
            We&apos;ve sent a 6-digit code to <strong className="text-gray-900">{email}</strong>
          </p>
          <p className="text-sm text-gray-600">Enter the code below to continue</p>
        </div>

        <FormField
          control={form.control}
          name="code"
          rules={{ required: 'Code is required', minLength: { value: 6, message: 'Code must be 6 digits' } }}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="block text-center text-gray-900">Verification Code</FormLabel>
              <FormControl>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={field.value}
                    onChange={field.onChange}
                    className="[&_input]:border-gray-300 [&_input]:bg-white [&_input]:text-gray-900 [&_input]:focus:border-blue-500 [&_input]:focus:ring-blue-500"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-3">
          <Button
            type="submit"
            className="w-full bg-blue-600 text-white hover:bg-blue-700"
            disabled={isSubmitDisabled}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Continue'
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            onClick={() => onResend()}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Resend Code'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
